/**
 * SuggestionChip — compact one-click affordance that surfaces an
 * `IntentSuggestion<T>` next to a "Suggest Best Option" / 'undecided'
 * dropdown.
 *
 * Used by the Rerun wizard's RerunProjectIntentStep + the New Report
 * wizard's intent surface to render the platform's pre-suggester
 * output (`suggestProjectIntent`). Hover tooltip surfaces the full
 * rationale + source; click pins the value and shifts the dropdown
 * out of 'undecided'.
 *
 * Pure presentational — caller owns the suggestion state + accept
 * callback; chip just renders + dispatches.
 */
import React from 'react';
import { Sparkles } from 'lucide-react';
import type { IntentSuggestionSource } from '../../lib/suggestProjectIntent';

export interface SuggestionChipProps<T extends string> {
  /** Suggestion's resolved value (passed through verbatim into onAccept). */
  value: T;
  /** Human-readable label for `value` — e.g. "Primary" for 'primary',
   *  "AI Training" for 'ai-training'. Falls back to the value if absent. */
  valueLabel?: string;
  /** ≤140-char rationale string from `suggestProjectIntent`. */
  rationale: string;
  /** 0-100. Shown in the chip caption + tooltip. */
  confidence: number;
  /** Provenance tier from `suggestProjectIntent`. Shown in the tooltip. */
  source: IntentSuggestionSource;
  /** Click handler — caller pins the value + records the source on the
   *  parent's projectIntent (`fieldSource[k] = source`). */
  onAccept: () => void;
  /** Optional test id for integration tests. Defaults to "suggestion-chip". */
  testId?: string;
  /** Optional aria-label override; defaults to "Apply suggestion: {valueLabel}". */
  ariaLabel?: string;
}

const SOURCE_LABEL: Record<IntentSuggestionSource, string> = {
  'prior-report': 'prior report',
  'document-extracted': 'sales deck',
  'site-context': 'site context',
  'portfolio-precedent': 'similar sites',
  'llm-inferred': 'AI inference',
  'archetype-default': 'archetype default',
  'dam-default': 'Datacrate Model',
  'fallback': 'platform default',
};

export function SuggestionChip<T extends string>({
  value,
  valueLabel,
  rationale,
  confidence,
  source,
  onAccept,
  testId = 'suggestion-chip',
  ariaLabel,
}: SuggestionChipProps<T>) {
  const label = valueLabel ?? String(value);
  const sourceLabel = SOURCE_LABEL[source] ?? source;
  const tooltip = `${rationale} (${confidence}% confidence · source: ${sourceLabel})`;
  return (
    <button
      type="button"
      onClick={onAccept}
      className="type-body uppercase tracking-widest rounded-md border border-primary/30 bg-primary/5 hover:bg-primary/10 px-2 py-1 inline-flex items-center gap-1 text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
      title={tooltip}
      aria-label={ariaLabel ?? `Apply suggestion: ${label}`}
      data-testid={testId}
      data-source={source}
      data-value={value}
      data-confidence={confidence}
    >
      <Sparkles className="w-3 h-3 text-primary" aria-hidden="true" />
      <span>{label}</span>
      <span aria-hidden="true">·</span>
      <span>{confidence}%</span>
    </button>
  );
}
